import { Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import {
  BackofficeDetailPromoItemWithDiscount,
  BackofficeDetailPromoItemWithFreeGoods,
  GroupedPromoItem,
} from '@diamond/shared/types';
import React from 'react';

import { isDiscount, isFreeGoods } from '../../utils';

interface PromoDetailTableMechanismProps {
  data: GroupedPromoItem[];
}

/**
 * PromoDetailTableMechanism Component
 * Displays a table with promotion details
 * Handles both discount promotions and free goods promotions
 */
export default function PromoDetailTableMechanism({
  data,
}: PromoDetailTableMechanismProps) {
  const hasFreeGoods = isFreeGoods(data);
  const hasDiscount = isDiscount(data);

  const renderTableHeader = () => (
    <Thead>
      <Tr>
        <Th w="5%">No.</Th>
        <Th width="20%">Kode Promo Satuan</Th>
        <Th w="50%">Product</Th>
        {hasFreeGoods && <Th w="25%">Produk Gratis</Th>}
      </Tr>
    </Thead>
  );

  const renderTableBody = () => {
    if (!data || data.length === 0) {
      return (
        <Tbody>
          <Tr>
            <Td colSpan={hasFreeGoods ? 4 : 3} textAlign="center">
              <Text color="gray.500">Tidak Ada Data</Text>
            </Td>
          </Tr>
        </Tbody>
      );
    }
    return (
      <Tbody>
        {hasDiscount && generateDiscountGoodsRows(data)}
        {hasFreeGoods && generateFreeGoodsRows(data)}
      </Tbody>
    );
  };

  return (
    <Table variant="promoHighlights">
      {renderTableHeader()}
      {renderTableBody()}
    </Table>
  );
}

/**
 * Generates table rows for discount promotions
 *
 * @param data - Array of discount promotion items
 * @returns Array of table row elements
 */
function generateDiscountGoodsRows(
  data: Array<BackofficeDetailPromoItemWithDiscount>
): Array<React.ReactElement> {
  return data.map((item, i) => {
    return (
      <Tr key={i}>
        <Td>{i + 1}</Td>
        <Td>{item.product_discount.promo_code}</Td>
        <Td>
          {item.product.title} ({item.product.item_code})
        </Td>
      </Tr>
    );
  });
}

/**
 * Generates table rows for free goods promotions
 *
 * @param data - Array of free goods promotion items
 * @returns Array of table row elements
 */
function generateFreeGoodsRows(
  data: Array<BackofficeDetailPromoItemWithFreeGoods>
): Array<React.ReactElement> {
  return data.map((item, i) => {
    return (
      <Tr key={i}>
        <Td>{i + 1}</Td>
        <Td>{item.product_free_good.promo_code}</Td>
        <Td>
          {item.product.title} ({item.product.item_code})
        </Td>
        <Td>
          {item.product_free_good.title} (
          {item.product_free_good.free_goods_product_item_code})
        </Td>
      </Tr>
    );
  });
}
